import { useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatDateTime } from '@brimo/shared';
import { Banner, Button, Pill, Skeleton, Textarea, useToast } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useDecideApproval, type ApprovalDetail } from '../approvals/api';
import { executeGdprRequest, useGdprRequest, type GdprRequestView } from './api';
import styles from './gdpr.module.css';

export interface GdprApprovalPanelProps {
  approval: ApprovalDetail;
  /** Called after „Löschung freigeben" / „Ablehnen" went through. */
  onDecided?: (() => void) | undefined;
}

const STATUS_PILL: Record<GdprRequestView['status'], 'warning' | 'info' | 'success' | 'danger'> = {
  open: 'warning',
  reviewed: 'info',
  deleted: 'success',
  rejected: 'danger',
};

function statusLine(request: GdprRequestView): string | null {
  switch (request.status) {
    case 'reviewed':
      return request.log ? request.log : 'Freigegeben · wird ausgeführt';
    case 'deleted':
      return request.executedAt ? `Gelöscht am ${formatDate(request.executedAt)}` : 'Gelöscht';
    case 'rejected':
      return 'Abgelehnt · die Daten bleiben erhalten, der Kontakt bleibt gesperrt';
    case 'open':
      return null;
  }
}

/**
 * „DSGVO-Löschung" inside the approvals dialog (Backoffice 09 panel layout; no frame): the contact, who requested
 * it and when, the reason, what is deleted / pseudonymized / kept (`GET /gdpr-requests?approvalId=`), „Ablehnen"
 * (reason) and „Löschung freigeben" through the generic `POST /approvals/:id/decide`; afterwards the state of the
 * minutely deletion job and its log.
 */
export function GdprApprovalPanel({ approval, onDecided }: GdprApprovalPanelProps) {
  const { toast } = useToast();
  const request = useGdprRequest(approval.id);
  const decide = useDecideApproval();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);

  const open = approval.status === 'open';
  const data = request.data;

  function submit(decision: 'approved' | 'rejected') {
    if (decision === 'rejected' && reason.trim() === '') {
      setReasonError('Bitte geben Sie einen Grund an.');
      return;
    }
    setReasonError(null);
    setError(null);
    decide.mutate(
      {
        id: approval.id,
        body: { decision, reason: decision === 'rejected' ? reason.trim() : null },
      },
      {
        onSuccess: () => {
          setRejecting(false);
          void request.refetch();
          onDecided?.();
        },
        onError: (cause) => {
          if (cause instanceof ApiError && cause.fields['reason']) setReasonError(cause.fields['reason']);
          else setError(errorMessage(cause));
        },
      },
    );
  }

  async function execute() {
    if (!data) return;
    setExecuting(true);
    try {
      await executeGdprRequest(data.id);
      toast({ message: 'Die Löschung wird erneut ausgeführt.' });
      await request.refetch();
    } catch (cause) {
      toast({ message: errorMessage(cause) });
    } finally {
      setExecuting(false);
    }
  }

  if (request.isPending) {
    return (
      <div className={styles.stack} aria-busy="true">
        <Skeleton width="60%" />
        <Skeleton width="45%" />
        <Skeleton variant="block" height={120} />
      </div>
    );
  }
  if (request.isError || !data) {
    return (
      <Banner
        tone="danger"
        actions={
          <Button variant="secondary" size="small" onClick={() => void request.refetch()}>
            Erneut laden
          </Button>
        }
      >
        {errorMessage(request.error)}
      </Banner>
    );
  }

  const line = statusLine(data);
  const failed = data.status === 'reviewed' && data.log !== null;

  return (
    <div className={styles.stack}>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className={styles.head}>
        <Pill variant={STATUS_PILL[data.status]}>{data.statusLabel}</Pill>
        <span className={styles.meta}>
          {data.requestedBy ? `${data.requestedBy.name} · ` : ''}
          {formatDateTime(data.requestedAt)}
          {open ? ` · wartet seit ${approval.waitingLabel}` : ''}
        </span>
        {open && approval.decidable && !rejecting ? (
          <span className={styles.actions}>
            <Button variant="secondary" disabled={decide.isPending} onClick={() => setRejecting(true)}>
              Ablehnen
            </Button>
            <Button loading={decide.isPending} onClick={() => submit('approved')}>
              Löschung freigeben
            </Button>
          </span>
        ) : null}
      </div>
      {open && !approval.decidable && approval.blockedReason ? (
        <Banner tone="info">{approval.blockedReason}</Banner>
      ) : null}
      {line ? (
        <div className={failed ? styles.statusFailed : styles.status} role="status">
          {line}
          {failed ? (
            <Button variant="secondary" size="small" loading={executing} onClick={() => void execute()}>
              Löschung erneut ausführen
            </Button>
          ) : null}
        </div>
      ) : null}
      <dl className={styles.rows}>
        <div className={styles.row}>
          <dt>Kontakt</dt>
          <dd>
            <Link to={`/contacts/${data.contact.id}`}>{data.contact.name}</Link>
          </dd>
        </div>
        <div className={styles.row}>
          <dt>Beantragt</dt>
          <dd>{`von ${data.requestedBy?.name ?? 'System'} am ${formatDate(data.requestedAt)}`}</dd>
        </div>
        {approval.message ? (
          <div className={styles.row}>
            <dt>Grund</dt>
            <dd>{approval.message}</dd>
          </div>
        ) : null}
        {!open && approval.decidedBy ? (
          <div className={styles.row}>
            <dt>Entschieden</dt>
            <dd>
              {`${approval.decidedBy.name}${approval.decidedAt ? ` · ${formatDateTime(approval.decidedAt)}` : ''}`}
              {approval.reason ? ` · ${approval.reason}` : ''}
            </dd>
          </div>
        ) : null}
      </dl>
      <div className={styles.lists}>
        <section className={styles.list} aria-label="Wird gelöscht">
          <div className={styles.listTitle}>Wird gelöscht</div>
          <ul>
            {data.preview.deletes.map((entry) => (
              <li key={entry.label}>
                <span>{entry.label}</span>
                <span className={styles.count}>{entry.count}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className={styles.list} aria-label="Wird pseudonymisiert">
          <div className={styles.listTitle}>Wird pseudonymisiert</div>
          <ul>
            {data.preview.pseudonymizes.map((entry) => (
              <li key={entry.label}>
                <span>{entry.label}</span>
                <span className={styles.count}>{entry.count}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className={styles.list} aria-label="Bleibt erhalten">
          <div className={styles.listTitle}>Bleibt erhalten</div>
          <ul>
            {data.preview.keeps.map((label) => (
              <li key={label}>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      {data.log && data.status === 'deleted' ? (
        <details className={styles.log}>
          <summary>Protokoll der Löschung</summary>
          <pre>{data.log}</pre>
        </details>
      ) : null}
      {rejecting ? (
        <div className={styles.reject}>
          <Textarea
            label="Grund der Ablehnung"
            required
            rows={3}
            autoFocus
            value={reason}
            error={reasonError ?? undefined}
            onChange={(event) => setReason(event.target.value)}
          />
          <span className={styles.actions}>
            <Button variant="secondary" disabled={decide.isPending} onClick={() => setRejecting(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={decide.isPending} onClick={() => submit('rejected')}>
              Ablehnung bestätigen
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
